import { RadioGroup } from "@progress/kendo-react-inputs";
import { Label } from "@progress/kendo-react-labels";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { useEffect, useState } from "react";
import { Company } from "../Company";
import { Configuration } from "./Configuration";
import {
  ConfigurationsFilters,
  ConfigurationsGridByProduct,
  ConfigurationsGridByCompany,
  IProductFilters,
} from ".";

interface IConfigurationsOverviewTabProps {
  companies: Company[];
  companyId: number;
  commodityTypes: Record<string, string>;
  configurations: Record<number, Configuration[]>;
  margins: any[];
  marginField: string;
  childMarginField?: string;
  productTypes: Record<string, string>;
  onChange: () => void;
}

const PRODUCT_VIEW = "Product";
const COMPANY_VIEW = "Company";

export const ConfigurationsOverviewTab = ({
  companies,
  companyId,
  commodityTypes,
  configurations,
  margins,
  marginField,
  childMarginField,
  productTypes,
  onChange,
}: IConfigurationsOverviewTabProps) => {
  const childCompanyIds =
    companies?.filter((c) => c.parentCompanyId === companyId).map((c) => c.id) ?? [];

  const [gridData, setGridData] = useState<any[]>([]);
  const [filter, setFilter] = useState<IProductFilters | null>(null);

  useEffect(() => {
    const selectedCommodities = filter?.commodities ?? [];
    const selectedProducts = filter?.products ?? [];
    const filterText = filter?.text ?? "";

    const newConfigs = (configurations[companyId] ?? [])
      .filter((c) => c.isActive)
      .filter(
        (c) => selectedCommodities.length === 0 || selectedCommodities.includes(c.commodityType),
      )
      .filter((c) => selectedProducts.length === 0 || selectedProducts.includes(c.productType))
      .sort((a, b) =>
        !a || !b
          ? 0
          : a.commodity.localeCompare(b.commodity) ||
            a.type.localeCompare(b.type) ||
            a.instrument.localeCompare(b.instrument),
      );

    const allConfigs = Object.values(configurations).flat();

    const gridConfigList = newConfigs
      .map((config) => {
        return companies
          .filter((comp) => comp.businessTypes.includes(config.businessTypeId))
          .map((comp) => {
            const self = allConfigs.find(
              (conf) => conf.companyId === comp.id && conf.productId === config.productId,
            );
            const parent = allConfigs.find(
              (conf) =>
                conf.companyId == comp.parentCompanyId && conf.productId === config.productId,
            );

            const item: any = self
              ? Configuration.create(
                  self,
                  Object.assign({}, self, { parentConfigurationId: parent?.configurationId ?? 0 }),
                )
              : Configuration.create(
                  Object.assign({}, config, {
                    configurationId: 0,
                    companyId: comp.id,
                    isActive: false,
                    isConfigured: false,
                  }),
                  Object.assign({}, config, {
                    company: comp.name,
                    parentConfigurationId: config.configurationId,
                  }),
                );

            if (marginField) {
              const margin = margins.find((m) => m.marginId === item[marginField]);
              item.margin = item.configurationId === 0 ? "" : margin?.longName || "";
              item.marginId = margin?.marginId;
              item.marginCompanyId = margin?.companyId;
            }

            if (childMarginField) {
              const margin = margins.find((m) => m.marginId === item[childMarginField]);
              item.childMargin = item.configurationId === 0 ? "" : margin?.longName || "";
              item.childMarginId = margin?.marginId;
              item.childMarginCompanyId = margin?.companyId;
            }

            return item;
          });
      })
      .flat()
      .filter((c) =>
        `${c.company} ${c.commodity} ${c.instrument} ${c.margin} ${c.childMargin}`
          .toLowerCase()
          .includes(filterText.toLowerCase()),
      );

    // Filter out unactionable configs
    const gridConfigListFiltered = gridConfigList.filter(
      (c) =>
        c.configurationId !== 0 ||
        (c.configurationId === 0 &&
          childCompanyIds.includes(c.companyId) &&
          newConfigs.find((c2) => c2.configurationId === c.parentConfigurationId)?.isEnabled()),
    );

    setGridData(gridConfigListFiltered);
  }, [configurations, filter, margins, marginField, childMarginField]);

  const [view, setView] = useState(PRODUCT_VIEW);

  const viewOptions = [
    { value: PRODUCT_VIEW, label: PRODUCT_VIEW },
    { value: COMPANY_VIEW, label: COMPANY_VIEW },
  ];

  return (
    <>
      <div style={{ margin: "1rem 0" }}>
        <ConfigurationsFilters
          commodityTypes={commodityTypes}
          productTypes={productTypes}
          onChangeFilters={setFilter}
        />
      </div>

      <Tooltip anchorElement="target" parentTitle={true} position={"top"}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Label>Group by</Label>
          <RadioGroup
            layout={"horizontal"}
            data={viewOptions}
            onChange={(e) => setView(e.value)}
            value={view}
          />
        </div>

        {view === PRODUCT_VIEW && (
          <ConfigurationsGridByProduct
            data={gridData}
            childCompanyIds={childCompanyIds}
            margins={margins}
            marginField={marginField}
            childMarginField={childMarginField}
            onChange={onChange}
          />
        )}

        {view === COMPANY_VIEW && (
          <ConfigurationsGridByCompany
            companies={companies}
            companyId={companyId}
            data={gridData}
            margins={margins}
            marginField={marginField}
            childMarginField={childMarginField}
            onChange={onChange}
          />
        )}
      </Tooltip>
    </>
  );
};
